import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';

/** @type {WeakMap<HTMLElement, () => void>} */
const resizeHandlers = new WeakMap();

/** @type {WeakMap<HTMLElement, number>} last equalized height per section */
const appliedHeights = new WeakMap();

/**
 * @param {HTMLElement} section
 */
export function equalizeCaseStudyDetailRelatedCardHeights(section) {
	const cards = [...section.querySelectorAll('.cs-detail-related__slide .cs-grid-card')];
	if (!cards.length) {
		return;
	}

	cards.forEach((card) => {
		card.style.minHeight = '';
	});

	const maxHeight = Math.max(
		...cards.map((card) => Math.ceil(card.getBoundingClientRect().height)),
		0,
	);

	if (maxHeight > 0) {
		const heightPx = `${maxHeight}px`;
		cards.forEach((card) => {
			card.style.minHeight = heightPx;
		});
	}

	// Poke Swiper only when the equalized height actually changed: iOS fires
	// height-only resize storms while the toolbar collapses during vertical
	// page scroll, and in cssMode swiper.update() writes wrapper.scrollLeft,
	// so churning it fought native scroll-snap and jittered the carousel.
	const previous = appliedHeights.get(section);
	appliedHeights.set(section, maxHeight);
	if (previous === maxHeight) {
		return;
	}

	const swiperEl = section.querySelector('.js-cs-detail-related-swiper');
	if (swiperEl?.swiper) {
		swiperEl.swiper.update();
	}
}

/**
 * @param {HTMLElement} section
 */
function bindEqualHeightResize(section) {
	const existing = resizeHandlers.get(section);
	if (existing) {
		window.removeEventListener('resize', existing);
	}

	let frame = 0;
	// Height-only resizes are the iOS toolbar during vertical page scroll —
	// card heights depend on the viewport WIDTH, so only re-equalize when it
	// actually changed.
	let lastWidth = window.innerWidth;
	const onResize = () => {
		if (window.innerWidth === lastWidth) {
			return;
		}
		lastWidth = window.innerWidth;
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(() => equalizeCaseStudyDetailRelatedCardHeights(section));
	};

	resizeHandlers.set(section, onResize);
	window.addEventListener('resize', onResize, { passive: true });
}

/**
 * @param {HTMLElement} section
 */
function initSectionSwiper(section) {
	const el = section.querySelector('.js-cs-detail-related-swiper');
	const prevBtn = section.querySelector('.js-cs-detail-related-prev');
	const nextBtn = section.querySelector('.js-cs-detail-related-next');
	const nav = section.querySelector('.cs-detail-related__nav');
	const slides = el?.querySelectorAll('.swiper-slide') ?? [];

	if (!el) {
		return null;
	}

	if (slides.length < 2) {
		nav?.setAttribute('hidden', 'hidden');
		equalizeCaseStudyDetailRelatedCardHeights(section);
		return null;
	}

	// Hide the nav when all slides fit (locked); show it when scrolling is needed.
	const toggleNav = (sw) => nav?.toggleAttribute('hidden', sw.isLocked);

	// iOS/WebKit chokes on transform-mode carousels (blank/frozen slides,
	// dead nav — see client-testimonials-swiper.js for the history). Native
	// scroll + scroll-snap on touch devices sidesteps that bug family.
	const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;

	const swiper = new Swiper(el, {
		modules: [Navigation],
		slidesPerView: 'auto',
		spaceBetween: 20,
		watchOverflow: true,
		cssMode: isCoarsePointer,
		// Keep the drag on the carousel instead of losing it to iOS Safari's
		// edge swipe-back gesture (#128).
		edgeSwipeDetection: 'prevent',
		navigation: {
			prevEl: prevBtn,
			nextEl: nextBtn,
		},
		on: {
			init(sw) {
				toggleNav(sw);
				equalizeCaseStudyDetailRelatedCardHeights(section);
			},
			lock: toggleNav,
			unlock: toggleNav,
			resize(sw) {
				toggleNav(sw);
				equalizeCaseStudyDetailRelatedCardHeights(section);
			},
		},
	});

	bindEqualHeightResize(section);
	equalizeCaseStudyDetailRelatedCardHeights(section);

	return swiper;
}

export function initCaseStudyDetailRelatedSwiper() {
	document.querySelectorAll('.js-cs-detail-related-section').forEach((section) => {
		const el = section.querySelector('.js-cs-detail-related-swiper');
		if (!el || el.swiper) {
			return;
		}

		initSectionSwiper(section);
	});
}
